import {
    Badge,
    Button,
    Editable,
    FILockClosed,
    Flexer,
    Footer,
    FormLabel,
    getKey,
    Header,
    Heading,
    Icon,
    If,
    Pill,
    Popover,
    Portal,
    ProgressPie,
    Sidebar,
    Stack,
    Tab,
    TabList,
    Text,
    Textarea,
    Tooltip,
    useAlert,
    useInput,
    useStorage,
    useVisibility,
    View,
} from '@fold-dev/core'
import { useEffect, useMemo, useState } from 'react'
import {
    TbCalendar,
    TbCheck,
    TbDots,
    TbFiltersFilled,
    TbInbox,
    TbLayoutKanban,
    TbLayoutSidebar,
    TbLayoutSidebarFilled,
    TbLock,
    TbMap,
    TbMessageCircle,
    TbNote,
    TbPaperclip,
    TbPlus,
    TbSquareRoundedCheck,
    TbStar,
    TbStarFilled,
    TbWorld,
    TbX
} from 'react-icons/tb'
import { Outlet, useNavigate, useParams } from 'react-router'
import { Block, Error, Filter, getSavedFilters, Inbox, ProjectUserPreview, Tasks, TeamUserPreview } from '../components'
import { Messages } from '../components/messages'
import { Overview } from '../components/overview'
import { NOTIFICATION_ACTION } from '../constants'
import {
    getFastCache,
    getInitials,
    isRichTextBlank,
    logger,
    setCachedProjectView,
    useDocumentTitle
} from '../helpers/util'
import { useNotifications, useProjects, useProjectUsers, useWorkspaceUsers } from '../hooks/api.hooks'
import { useAuth } from '../hooks/auth.hook'
import { useSubscription } from '../hooks/websocket.hook'
import { ProjectMenu } from '../menus/project.menu'
import { InviteModal } from '../modals/invite.modal'
import { NewTaskModal } from '../modals/new-task.modal'
import { ProjectModal } from '../modals/project.modal'
import { useAppStore } from '../store/app'
import { SidebarArea } from './sidebar.layout'

const AI = (props) => {
    const { workspaceId, projectId } = useParams()
    const menu = useVisibility()
    const description = useInput('')
    const { generate } = useProjects()
    const { alert } = useAlert()

    const handleCreate = async () => {
        handleDismiss()
        try {
            await generate(workspaceId, projectId, description.value)
        } catch (e) {
            alert({
                title: 'Sorry',
                description: 'We did not understand that request.',
                onDismiss: handleDismiss,
            })
        }
    }

    const handleKeyDown = (e) => {
        const { isEnter } = getKey(e)
        if (isEnter) handleCreate()
    }

    const handleDismiss = () => {
        menu.hide()
        description.setValue('')
    }

    return (
        <>
            <Popover
                m="0.5rem 0 0 0"
                focusTrap
                arrow
                width={300}
                portal={Portal}
                isVisible={menu.visible}
                onDismiss={handleDismiss}
                content={
                    <View width="100%">
                        <Stack
                            p="1rem"
                            width="100%"
                            direction="vertical"
                            spacing="0.75rem">
                            <View
                                row
                                gap="0.5rem"
                                justifyContent="flex-start"
                                width="100%">
                                <FormLabel htmlFor="ai">Generate tasks using AI</FormLabel>
                                <Pill
                                    subtle
                                    size="xs">
                                    BETA
                                </Pill>
                            </View>
                            <Textarea
                                id="ai"
                                autoAdjust
                                selectOnFocus
                                value={description.value}
                                minHeight={100}
                                placeholder="Create a list of stuff to do for launching my website."
                                onChange={description.onChange}
                                onKeyDown={handleKeyDown}
                            />
                        </Stack>

                        <Footer
                            p="0.5rem"
                            bg="transparent">
                            <Button
                                size="sm"
                                onClick={handleDismiss}>
                                Cancel
                            </Button>
                            <Flexer />
                            <Button
                                onClick={handleCreate}
                                variant="accent"
                                size="sm"
                                disabled={!description.value}>
                                General
                            </Button>
                        </Footer>
                    </View>
                }>
                <Button
                    subtle
                    width={26}
                    height={26}
                    m="0 5px 0 0"
                    onClick={menu.show}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 200 200"
                        width="24"
                        height="24"
                        style={{ filter: 'drop-shadow(0 0 5px rgba(43, 162, 237, 0.1))' }}>
                        <defs>
                            <linearGradient
                                id="outlineGradient"
                                x1="0%"
                                y1="0%"
                                x2="100%"
                                y2="100%">
                                <stop
                                    offset="0%"
                                    stopColor="oklch(65% 0.25 300)"
                                />
                                <stop
                                    offset="100%"
                                    stopColor="oklch(65% 0.25 240)"
                                />
                            </linearGradient>
                        </defs>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 200 200">
                            <defs>
                                <linearGradient
                                    id="animatedGradient"
                                    x1="0%"
                                    y1="0%"
                                    x2="100%"
                                    y2="100%">
                                    <stop
                                        offset="0%"
                                        stopColor="oklch(65% 0.25 300)">
                                        <animate
                                            attributeName="stopColor"
                                            values="oklch(65% 0.25 300);oklch(65% 0.25 240);oklch(65% 0.25 300)"
                                            dur="4s"
                                            repeatCount="indefinite"
                                        />
                                    </stop>
                                    <stop
                                        offset="100%"
                                        stopColor="oklch(65% 0.25 240)">
                                        <animate
                                            attributeName="stopColor"
                                            values="oklch(65% 0.25 240);oklch(65% 0.25 300);oklch(65% 0.25 240)"
                                            dur="4s"
                                            repeatCount="indefinite"
                                        />
                                    </stop>
                                </linearGradient>
                            </defs>
                            <circle
                                cx="100"
                                cy="100"
                                r="65"
                                fill="none"
                                stroke="url(#animatedGradient)"
                                strokeWidth="30">
                                <animateTransform
                                    attributeName="transform"
                                    type="rotate"
                                    from="0 100 100"
                                    to="360 100 100"
                                    dur="10s"
                                    repeatCount="indefinite"
                                />
                            </circle>
                        </svg>
                    </svg>
                </Button>
            </Popover>
        </>
    )
}

export const ProjectSidebar = () => {
    const { workspaceId, view, projectId, taskId } = useParams()
    const project = useAppStore((state) => state.project)
    const sidebar = useAppStore((state) => state.sidebar)
    const thread = useAppStore((state) => state.thread)
    const actions = useAppStore((state) => state.actions)
    const isProject = !project.isChannel
    const workspace = useAppStore((state) => state.workspace)
    const fields = useMemo(
        () => (workspace.workspaceUserFields || {})[projectId],
        [workspace.workspaceUserFields, projectId]
    )
    const { sidebarTasksView, showSidebarTasks } = useMemo(() => {
        return {
            sidebarTasksView: sidebar == 'tasks' ? 'list' : 'calendar',
            showSidebarTasks: sidebar == 'tasks' || sidebar == 'calendar',
        }
    }, [sidebar])

    // useEffect(() => {
    //     const isChannel = project.isChannel
    //     const isThread = !!thread.id
    //     const isProjectAndSidebarIsInvalid = !project.isChannel && sidebar == 'tasks'

    //     if (isChannel || isThread || isProjectAndSidebarIsInvalid) {
    //         actions.setSidebar('overview')
    //         actions.setThread({ id: null, messages: [] })
    //     }
    // }, [project.isChannel, thread.id, sidebar])

    return (
        <SidebarArea>
            {sidebar == 'overview' && <Overview />}

            {sidebar == 'messages' && (
                <Messages
                    isSidebar
                    teamId={project.teamId}
                    workspaceId={workspaceId}
                    projectId={projectId}
                />
            )}

            {sidebar == 'inbox' && (
                <Inbox
                    projectId={projectId}
                    workspaceId={workspaceId}
                    view={view}
                />
            )}

            {sidebar == 'thread' && (
                <Messages
                    isSidebar
                    workspaceId={workspaceId}
                    projectId={projectId}
                    teamId={project.teamId}
                />
            )}

            {showSidebarTasks && (
                <>
                    {sidebar != 'calendar' && (
                        <Header
                            bgToken="background"
                            height="var(---header-sidebar-height)"
                            p="0 1rem">
                            <View
                                width="100%"
                                position="relative"
                                row>
                                <Heading
                                    as="h5"
                                    style={{ textTransform: 'capitalize' }}>
                                    {sidebar == 'tasks' ? 'Tasks' : 'Calendar'}
                                </Heading>
                                <Flexer />
                                <Button
                                    size="xs"
                                    bgToken="surface-strong"
                                    onClick={() => actions.setSidebar(null)}>
                                    <Icon
                                        icon={TbX}
                                        size="sm"
                                    />
                                </Button>
                            </View>
                        </Header>
                    )}
                    <View
                        position="relative"
                        bgToken="sidebar"
                        flex={1}
                        width="100%"
                        height="100%">
                        <View
                            width="100%"
                            height="100%"
                            style={{ top: 0, left: 0 }}
                            position="absolute"
                            className="f-overflow-y-auto f-scrollbar">
                            <Tasks
                                isSidebar
                                dontLoadTasks={isProject}
                                view={sidebarTasksView}
                                workspaceId={workspaceId}
                                projectId={projectId}
                                teamId={project.teamId}
                                fields={fields}
                                type="project"
                            />
                        </View>
                    </View>
                </>
            )}
        </SidebarArea>
    )
}

const TeamHeader = (props) => {
    const {
        onAdd,
        onAddPeople,
        onSettings,
        onPeople,
        onInvite,
        hasNotifications,
        hasMessageNotifications,
        handleMessagesOpen,
        handleTasksOpen,
        handleCalendarOpen,
    } = props
    const { workspaceId, projectId } = useParams()
    const project = useAppStore((state) => state.project)
    const { id, color, archived, name, teamId, teamName, teamColor, isClosed, isPrivate } = project
    const projectsAPI = useProjects()
    const starred = useAppStore((state) => state.starred)
    const workspace = useAppStore((state) => state.workspace)
    const actions = useAppStore((state) => state.actions)
    const sidebar = useAppStore((state) => state.sidebar)
    const hideNav = useAppStore((state) => state.hideNav)
    const viewType = useAppStore((state) => state.viewType)
    const workspaceUsersAPI = useWorkspaceUsers()
    const menuPopup = useVisibility()
    const isStarred = useMemo(() => starred.findIndex((sid) => sid == projectId) != -1, [starred, projectId])
    const isGuest = useMemo(() => workspace.role == 'Guest', [workspace.role])
    const { textColor, text, icon } = useMemo(() => {
        return {
            icon: isClosed && !teamId ? FILockClosed : !!teamId ? TbFiltersFilled : TbWorld,
            text: isClosed && !teamId ? 'Private' : !!teamId ? teamName : 'Workspace',
            textColor: !!teamColor ? teamColor : 'var(--f-color-text-weaker)',
        }
    }, [teamId, teamName, teamColor, isClosed])

    const handleProjectChannelUpdate = async (e) => {
        try {
            const isChannel = !project.isChannel

            actions.projectUpdate({ id, isChannel })

            await projectsAPI.update(workspaceId, id, { isChannel })
        } catch (e) {
            logger(e)
        }
    }

    const handleUpdateStarred = async () => {
        const { id, icon, color, name } = project
        const updatedStarred = isStarred ? starred.filter((sid) => sid != id) : [...starred, id]

        // atomic state
        if (isStarred) {
            actions.starredDelete(id)
        } else {
            actions.starredAdd(id)
        }

        await workspaceUsersAPI.update(workspaceId, workspace.workspaceUserId, { starred: updatedStarred })
    }

    return (
        <Header
            height="var(---header-height)"
            p="0.75rem"
            border="0"
            bg="transparent">
            <View
                row
                m="0 0.5rem 0 0"
                gap="0.75rem"
                color={textColor}>
                <Button
                    subtle
                    size="sm"
                    height="2rem"
                    p="0 0.5rem"
                    m="0 -0.5rem 0 0"
                    fontWeight="bold"
                    onClick={() => actions.setHideNav(!hideNav)}
                    colorToken="text-weaker">
                    <Icon 
                        icon={!hideNav ? TbLayoutSidebar : TbLayoutSidebarFilled} 
                        style={!hideNav ? undefined : { '--f-icon-stroke-width-md': '0' }}
                    />
                </Button>
                <Text
                    size="sm"
                    fontWeight={600}
                    color="inherit">
                    Workspace
                </Text>
                {isClosed && !teamId && (
                    <Icon
                        icon={TbLock}
                        className="tabler-lock-icon"
                    />
                )}
                {!!teamId && (
                    <>
                        <Text
                            size="sm"
                            fontWeight={600}
                            color="inherit">
                            /
                        </Text>
                        <Text
                            size="sm"
                            fontWeight={600}
                            color="inherit">
                            {teamName}
                        </Text>
                    </>
                )}
                {/* 
                <Icon
                    icon={icon}
                    style={{ stroke: !!teamId ? 'none' : undefined }}
                />
                <Text
                    size="sm"
                    fontWeight={600}
                    color="inherit">
                    {text}
                </Text> 
                */}

                <Popover
                    arrow
                    anchor="bottom-left"
                    width="fit-content"
                    content={
                        <ProjectMenu
                            project={{ ...project }}
                            onDismiss={menuPopup.hide}
                            onInvite={() => {
                                onInvite()
                                menuPopup.hide()
                            }}
                            onSettings={() => {
                                onSettings()
                                menuPopup.hide()
                            }}
                        />
                    }
                    isVisible={menuPopup.visible}
                    onDismiss={menuPopup.hide}>
                    <Button
                        subtle
                        size="sm"
                        height="2rem"
                        p="0 0.5rem"
                        fontWeight="bold"
                        colorToken="text-weaker"
                        onClick={menuPopup.show}>
                        <Icon
                            icon={TbDots}
                            className="f-buttonize"
                        />
                    </Button>
                </Popover>
                
                {project.joinable && (
                    <Tooltip
                        text="Joinable"
                        anchor="bottom-center">
                        <View row>
                            <Icon
                                icon={TbWorld}
                                color="var(--f-color-text-weaker)"
                            />
                        </View>
                    </Tooltip>
                )}

                {project.roadmap && (
                    <Tooltip
                        text="Public Roadmap Enabled"
                        anchor="bottom-center">
                        <View row>
                            <Icon
                                icon={TbMap}
                                color="var(--f-color-text-weaker)"
                            />
                        </View>
                    </Tooltip>
                )}
            </View>

            <View
                m="0 0.5rem"
                gap="1rem"
                row>
                <Icon
                    icon={isStarred ? TbStarFilled : TbStar}
                    color={isStarred ? 'var(--f-color-yellow-400)' : 'var(--f-color-text-weaker)'}
                    onClick={handleUpdateStarred}
                    className="f-buttonize"
                />
            </View>

            <Flexer />

            {/*  
            <View
                row
                gap="0.5rem"
                m="0 0.5rem 0 0">
                <Tooltip
                    text={project.isChannel ? 'Make this a project' : 'Make this a channel'}
                    anchor="bottom-center"
                    delay={0}>
                    <View
                        row
                        width="fit-content">
                        <Toggle
                            size="sm"
                            id="closed"
                            disabled={!isAdmin}
                            on={project.isChannel}
                            onChange={handleProjectChannelUpdate}
                        />
                    </View>
                </Tooltip>
            </View> 

            <AI />
     
            {!teamId && !isGuest && (
                <Button
                    subtle
                    height="2rem"
                    size="sm"
                    p="0 0.5rem"
                    fontWeight={600}
                    colorToken="text-weaker"
                    onClick={onAddPeople}>
                    Add People
                </Button>
            )} 

            <Button
                subtle
                height="2rem"
                size="sm"
                p="0 0.5rem"
                fontWeight={600}
                colorToken="text-weaker"
                onClick={onSettings}>
                <Icon icon={TbSettings} />
            </Button>
            */}

            <Tooltip text="Project Members" anchor="bottom-center">
                <Button
                    subtle
                    size="sm"
                    height="2rem"
                    fontWeight="bold"
                    p="0 0.25rem"
                    m="0 0.25rem 0 0"
                    disabled={isGuest}
                    colorToken="text-weaker"
                    onClick={onPeople}>
                    {!!teamId && (
                        <Icon
                            icon={FILockClosed}
                            size="sm"
                            style={{
                                'color': 'var(--f-color-text-weakest)',
                                'marginRight': 5,
                                '--f-icon-stroke-width-sm': '3',
                            }}
                        />
                    )}

                    {!!teamId && (
                        <TeamUserPreview
                            teamId={teamId}
                            workspaceId={workspaceId}
                            size="sm"
                        />
                    )}

                    {!teamId && (
                        <ProjectUserPreview
                            projectId={projectId}
                            workspaceId={workspaceId}
                            size="sm"
                        />
                    )}
                </Button>
            </Tooltip>

            <Tooltip text="Inbox" anchor="bottom-center">
                <Button
                    subtle
                    height="2rem"
                    p="0 0.5rem"
                    bgToken={sidebar == 'inbox' ? 'surface-strong' : undefined}
                    colorToken="text-weaker"
                    onClick={() => actions.setSidebar('inbox')}>
                    <Icon icon={TbInbox} />
                    {hasNotifications && (
                        <Badge
                            anchor="top-right"
                            variant="accent"
                            bgToken="danger"
                            style={{ outline: '2px solid var(--f-color-surface)' }}
                            zIndex={10}
                        />
                    )}
                </Button>
            </Tooltip>

            {viewType != 'calendar' && (
                <Tooltip text="Calendar" anchor="bottom-center">
                    <Button
                        subtle
                        height="2rem"
                        p="0 0.5rem"
                        bgToken={sidebar == 'calendar' ? 'surface-strong' : undefined}
                        colorToken="text-weaker"
                        onClick={handleCalendarOpen}>
                        <Icon
                            icon={TbCalendar}
                            className="tabler-calendar-icon"
                        />
                    </Button>
                </Tooltip>
            )}

            {project.isChannel && (
                <Tooltip text="Tasks" anchor="bottom-center">
                    <Button
                        subtle
                        height="2rem"
                        p="0 0.5rem"
                        bgToken={sidebar == 'tasks' ? 'surface-strong' : undefined}
                        colorToken="text-weaker"
                        onClick={handleTasksOpen}>
                        <Icon icon={TbSquareRoundedCheck} />
                    </Button>
                </Tooltip>
            )}

            {!project.isChannel && (
                <Tooltip text="Messages" anchor="bottom-center">
                    <Button
                        subtle
                        height="2rem"
                        p="0 0.5rem"
                        bgToken={sidebar == 'messages' ? 'surface-strong' : undefined}
                        colorToken="text-weaker"
                        onClick={handleMessagesOpen}>
                        <Icon icon={TbMessageCircle} />
                        {hasMessageNotifications && (
                            <Badge
                                anchor="top-right"
                                variant="accent"
                                bgToken="danger"
                                style={{ outline: '2px solid var(--f-color-surface)' }}
                                zIndex={10}
                            />
                        )}
                    </Button>
                </Tooltip>
            )}

            <Tooltip text="Notes" anchor="bottom-center">
                <Button
                    subtle
                    height="2rem"
                    p="0 0.5rem"
                    bgToken={sidebar == 'overview' ? 'surface-strong' : undefined}
                    colorToken="text-weaker"
                    onClick={() => actions.setSidebar('overview')}>
                    <Icon icon={TbNote} />
                </Button>
            </Tooltip>
            
            <Button
                onClick={onAdd}
                size="sm"
                m="0 0 0 0.75rem"
                prefix={<Icon icon={TbPlus} />}>
                Add Task
            </Button> 
           
        </Header>
    )
}

const ProjectHeader = ({ children = null, onAdd, onInvite, onSettings }) => {
    const { workspaceId, projectId, view } = useParams()
    const project = useAppStore((state) => state.project)
    const { id, icon, color, archived, name, isClosed, isChannel, teamId, completeTaskCount, totalTaskCount, isPrivate } = project
    const actions = useAppStore((state) => state.actions)
    const showDescription = useMemo(() => {
        const content = project.description || ''
        if (isRichTextBlank(content)) return false
        return true
    }, [project.description])
    const projectsAPI = useProjects()
    const { getUserId } = useAuth()
    const projectName = useMemo(() => {
        const userId = getUserId()
        return project.isPrivate ? getInitials(project.projectUsers, userId) : project.name
    }, [project])
    const projectImage = useMemo(() => {
        const userId = getUserId()
        return project.isPrivate
            ? project.projectUsers.length == 2
                ? project.projectUsers.filter((u) => u.id != userId)[0].image
                : null
            : null
    }, [project.isPrivate, project.projectUsers])
    const projectUserId = useMemo(() => {
        const userId = getUserId()
        return project.isPrivate
            ? project.projectUsers.length == 2
                ? project.projectUsers.filter((u) => u.id != userId)[0].id
                : null
            : null
    }, [project.isPrivate, project.projectUsers])

    const handleProjectUpdate = async (name: any) => {
        try {
            actions.projectUpdate({ id: projectId, name })
            await projectsAPI.update(workspaceId, projectId, { name })
        } catch (e) {
            logger(e)
        }
    }

    const progress = useMemo(() => Math.floor(Math.random() * 100), [])

    useDocumentTitle(projectName)

    return (
        <>
            <Header
                row
                gap="0.25rem"
                p="2rem var(--gutter)"
                style={{ maxWidth: 'var(--max-width)' }}
                border="none"
                bg="transparent"
                justifyContent="flex-start"
                className="project-header">
                <View
                    row
                    m="0 0 0 0"
                    gap="0.5rem"
                    className="reveal-on-hover">
                    {isPrivate && (
                        <Block
                            icon={icon}
                            color={color}
                            name={projectName}
                            size="md"
                            src={projectImage}
                            isPrivate={project.isPrivate}
                            isChannel={project.isChannel}
                            otherUserId={projectUserId}
                        />
                    )}

                    {!isPrivate && (
                         <ProgressPie
                             value={completeTaskCount / totalTaskCount * 100}
                             size={25}
                             padding={2}
                             radius={50}
                             style={{
                                 '--f-progress-background': 'var(--f-color-surface)',
                                 '--f-progress-active': color || 'var(--f-color-text-weaker)',
                                 'border': '2px solid ' + (color || 'var(--f-color-text-weaker)'),
                             }}
                         />
                    )}

                    <View
                        column
                        p="0 0 0 0.75rem"
                        position="relative"
                        style={{ top: 1 }}
                        alignItems="flex-start">
                        <Editable
                            onChange={handleProjectUpdate}
                            disabled={project.isPrivate}>
                            <Heading
                                as="h3"
                                className="main-heading">
                                {projectName}
                            </Heading>
                        </Editable>
                    </View>

                    <If if={archived}>
                        <Pill
                            outline
                            m="0 0 0 1rem"
                            size="sm">
                            ARCHIVED
                        </Pill>
                    </If>

                    {!showDescription && (
                        <Button
                            onClick={onSettings}
                            subtle
                            size="sm"
                            className="reveal-on-hover-target"
                            width={130}>
                            Add Description
                        </Button>
                    )}
                </View>

                {/* 
                <Icon
                    className="f-buttonize"
                    onClick={handleUpdateStarred}
                    icon={isStarred ? TbPinFilled : TbPin}
                    color={isStarred ? color || 'var(--f-color-accent)' : 'var(--f-color-text-weaker)'}
                /> 
                <Popover
                    arrow
                    width="fit-content"
                    content={
                        <ProjectMenu
                            project={{ id, name, archived, color, icon, starred }}
                            onDismiss={menuPopup.hide}
                            onInvite={() => {
                                onInvite()
                                menuPopup.hide()
                            }}
                            onSettings={() => {
                                onSettings()
                                menuPopup.hide()
                            }}
                        />
                    }
                    isVisible={menuPopup.visible}
                    onDismiss={menuPopup.hide}>
                    <Button
                        subtle
                        height="2rem"
                        width="2rem"
                        onClick={menuPopup.show}>
                        <Icon
                            icon={EllipsisHorizontalIcon}
                            className="f-buttonize"
                        />
                    </Button>
                </Popover>
                */}

                {!showDescription && <div className="f-flexer" />}

                {showDescription && (
                    <View
                        p="0 0 0 1rem"
                        m="0 auto 0 0"
                        className="f-buttonize"
                        onClick={(e: any) => {
                            if (!e.target?.href) onSettings()
                        }}>
                        <Text
                            size="sm"
                            dangerouslySetInnerHTML={{ __html: project.description }}
                        />
                    </View>
                )}

                {children}
       
                {/*     
                {!isChannel && (
                    <Button
                        onClick={onAdd}
                        size="sm"
                        m="0 0 0 0.75rem"
                        variant="accent"
                        prefix={<Icon icon={TbPlus} />}>
                        Add Task
                    </Button>
                )} 
                */}
            </Header>
        </>
    )
}

const ToolHeader = ({ view, onViewChange }) => {
    return (
        <Header
            row
            gap={5}
            bgToken="surface"
            zIndex={100}
            justifyContent="flex-start"
            p="0rem 0 0 5rem"
            style={{ borderColor: 'var(--f-color-surface-stronger)' }}>
            <TabList
                justifyContent="flex-start"
                selected={view}
                onSelect={onViewChange}
                width="fit-content"
                border="0"
                style={{
                    '--f-tabs-tab-padding': '0rem',
                    '--f-tabs-tab-background-hover': 'transparent',
                    '--f-tabs-tab-background-active': 'transparent',
                }}>
                <Tab
                    flex={0}
                    width="fit-content"
                    m="0 1rem 0 0"
                    prefix={
                        <Icon
                            icon={TbInbox}
                            size="sm"
                        />
                    }>
                    Inbox
                </Tab>
                <Tab
                    flex={0}
                    width="fit-content"
                    m="0 1rem 0 0"
                    prefix={
                        <Icon
                            icon={TbMessageCircle}
                            size="sm"
                        />
                    }>
                    Chat
                </Tab>
                <Tab
                    flex={0}
                    width="fit-content"
                    m="0 1rem 0 0"
                    prefix={
                        <Icon
                            icon={TbCheck}
                            size="sm"
                        />
                    }>
                    List
                </Tab>
                <Tab
                    flex={0}
                    width="fit-content"
                    m="0 1rem 0 0"
                    prefix={
                        <Icon
                            icon={TbLayoutKanban}
                            style={{ '--f-icon-sizing-md': '17px' }}
                        />
                    }>
                    Board
                </Tab>
                <Tab
                    flex={0}
                    width="fit-content"
                    m="0 1rem 0 0"
                    prefix={
                        <Icon
                            icon={TbCalendar}
                            size="sm"
                        />
                    }>
                    Calendar
                </Tab>
                <Tab
                    flex={0}
                    width="fit-content"
                    m="0 1rem 0 0"
                    prefix={
                        <Icon
                            icon={TbPaperclip}
                            size="sm"
                        />
                    }>
                    Files
                </Tab>
                <Flexer />
            </TabList>
        </Header>
    )
}

export const ProjectLayout = (props: any) => {
    const { workspaceId, view, projectId, taskId } = useParams()
    const [projectModal, setProjectModal] = useState(null)
    const inviteModal = useVisibility()
    const addModal = useVisibility()
    const { getOne } = useProjects()
    const { setStorage, getStorage } = useStorage()
    const projectUsersAPI = useProjectUsers()
    const project = useAppStore((state) => state.project)
    const projects = useAppStore((state) => state.projects)
    const viewType = useAppStore((state) => state.viewType)
    const sidebar = useAppStore((state) => state.sidebar)
    const actions = useAppStore((state) => state.actions)
    const workspace = useAppStore((state) => state.workspace)
    const fields = useMemo(
        () => (workspace.workspaceUserFields || {})[projectId],
        [workspace.workspaceUserFields, projectId]
    )
    const navigate = useNavigate()
    const { getUserId } = useAuth()
    const [loading, setLoading] = useState(false)
    const notificationsAPI = useNotifications()
    const [error, setError] = useState<any>(null)
    const selectedViewOption = useMemo(() => {
        switch (view) {
            case 'list':
                return 0
            case 'board':
                return 1
            case 'calendar':
                return 2
            case 'timeline':
                return 3
            default:
                return -1
        }
    }, [view])
    const notifications = useAppStore((state) => state.notifications)
    const { hasMessageNotifications, hasNotifications } = useMemo(() => {
        const projectNotifications = notifications[projectId] || []
        return {
            projectNotifications,
            hasMessageNotifications: !!projectNotifications.filter(
                (n) => !n.read && n.action == NOTIFICATION_ACTION.PROJECT.MESSAGE_MENTION
            ).length,
            hasNotifications: projectNotifications.some((n) => !n.read),
        }
    }, [projectId, notifications])

    const handleCalendarOpen = async () => {
        actions.setSidebar('calendar')
    }

    const handleTasksOpen = async () => {
        actions.setSidebar('tasks')
    }

    const handleMessagesOpen = async () => {
        try {
            actions.setSidebar('messages')

            // if this project has any mention notifications
            // then remove them from the store & also remove them from the DB
            if (notifications[projectId]) {
                const hasMessageMentionNotification = notifications[projectId].some(
                    (n) => n.action == NOTIFICATION_ACTION.PROJECT.MESSAGE_MENTION
                )

                // remove the notification if there is one
                if (hasMessageMentionNotification) {
                    actions.setNotifications({
                        ...notifications,
                        [projectId]: notifications[projectId].filter(
                            (n) => n.action != NOTIFICATION_ACTION.PROJECT.MESSAGE_MENTION
                        ),
                    })

                    await notificationsAPI.removeProjectMessageMention(workspaceId, projectId)
                }
            }
        } catch (e) {
            logger(e)
            setError('Could not remove notification')
        }
    }

    const fetchData = async () => {
        setLoading(true)
        setError(null)

        try {
            const filters = getSavedFilters(projectId)
            const projectList = projects.find((project) => project.id == projectId)

            // window[ABORT_CONTROLLER]?.abort('fetch interupt')

            actions.setSections(getFastCache(projectId), false)
            actions.setProject({ ...project, ...projectList })
            actions.setFilters(filters)
            actions.setViewType('project')

            // if it's already open then clear notifications
            if (sidebar == 'messages') handleMessagesOpen()

            setLoading(false)

            // we fetch the proper one like this because
            // we don't want to delay the tasks API call with this one
            getOne(workspaceId, projectId).then((project) => {
                if (project.isPrivate) {
                    projectUsersAPI.getAll(workspaceId, projectId).then((projectUsers) => {
                        actions.setProject({ ...project, projectUsers })
                    })
                } else {
                    actions.setProject(project)
                }
            })
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const navigateToView = (view: string) => {
        setCachedProjectView(projectId, view)
        navigate(`/app/workspace/${workspaceId}/c/${projectId}/${view}`)
    }

    const handleViewChange = (index) => {
        switch (index) {
            case 0:
                return navigateToView('list')
            case 1:
                return navigateToView('board')
            case 2:
                return navigateToView('calendar')
            case 3:
                return navigateToView('timeline')
        }
    }

    useSubscription(projectId)

    useEffect(() => {
        if (projectId) fetchData()
    }, [projectId])

    if (!project) return null

    return (
        <>
            {projectModal != null && (
                <ProjectModal
                    projectId={project.id}
                    defaultTab={projectModal}
                    onDismiss={() => setProjectModal(null)}
                    onDelete={() => setProjectModal(null)}
                />
            )}

            {inviteModal.visible && (
                <InviteModal
                    projectId={project.id}
                    onDismiss={inviteModal.hide}
                />
            )}

            {addModal.visible && (
                <NewTaskModal
                    type="project"
                    teamId={project.teamId}
                    onDismiss={addModal.hide}
                    selectSection
                />
            )}

            <View
                row
                flex={1}
                width="100%"
                height="calc(100% - (var(--frame-padding) * 2))"
                justifyContent="flex-start"
                position="relative"
                m={!!sidebar ? "var(--frame-padding) 0" : "var(--frame-padding) var(--frame-padding) var(--frame-padding) 0"}
                bgToken="surface"
                style={{
                    overflow: 'hidden',
                    borderTopLeftRadius: 'var(--frame-radius)',
                    borderTopRightRadius: 'var(--frame-radius)',
                    borderBottomLeftRadius: 'var(--frame-radius)',
                    borderBottomRightRadius: 'var(--frame-radius)',
                    border: 'var(--frame-border)',
                }}>
                <View
                    column
                    flex={1}
                    height="100%"
                    width="100%"
                    position="relative"
                    justifyContent="stretch">
                    <TeamHeader
                        onAdd={addModal.show}
                        onSettings={() => setProjectModal(0)}
                        onPeople={() => setProjectModal(2)}
                        onAddPeople={inviteModal.show}
                        onInvite={inviteModal.show}
                        hasNotifications={hasNotifications}
                        hasMessageNotifications={hasMessageNotifications}
                        handleMessagesOpen={handleMessagesOpen}
                        handleTasksOpen={handleTasksOpen}
                        handleCalendarOpen={handleCalendarOpen}
                    />

                    <ProjectHeader
                        onAdd={addModal.show}
                        onInvite={inviteModal.show}
                        onSettings={() => setProjectModal(0)}>
                        {/* 
                        <Button
                            subtle
                            bgToken={selectedViewOption == 0 ? 'surface-strong' : undefined}
                            onClick={() => handleViewChange(0)}>
                            <Icon icon={TbSquareRoundedCheck} />
                        </Button>
                        <Button
                            subtle
                            bgToken={selectedViewOption == 1 ? 'surface-strong' : undefined}
                            onClick={() => handleViewChange(1)}>
                            <Icon icon={TbLayoutCards} />
                        </Button>
                        <Button
                            subtle
                            bgToken={selectedViewOption == 2 ? 'surface-strong' : undefined}
                            onClick={() => handleViewChange(2)}>
                            <Icon
                                icon={TbCalendar}
                                className="tabler-calendar-icon"
                            />
                        </Button>
                        <Button
                            subtle
                            bgToken={selectedViewOption == 3 ? 'surface-strong' : undefined}
                            onClick={() => handleViewChange(3)}>
                            <Icon icon={TbMenu3} />
                        </Button>               
                        <Button
                            subtle
                            width={36}
                            height={36}
                            bgToken={sidebar == 'overview' ? 'surface-strong' : undefined}
                            onClick={() => actions.setSidebar('overview')}>
                            <Icon icon={TbNote} />
                        </Button>
                        <Button
                            subtle
                            width={36}
                            height={36}
                            bgToken={sidebar == 'inbox' ? 'surface-strong' : undefined}
                            onClick={() => actions.setSidebar('inbox')}>
                            <Icon icon={TbInbox} />
                            {hasNotifications && (
                                <Badge
                                    anchor="bottom-right"
                                    variant="accent"
                                    bgToken="danger"
                                />
                            )}
                        </Button>
                        <Button
                            subtle
                            width={36}
                            height={36}
                            bgToken={sidebar == 'messages' ? 'surface-strong' : undefined}
                            onClick={handleMessagesOpen}>
                            <Icon icon={TbMessageCircle} />
                            {hasMessageNotifications && (
                                <Badge
                                    anchor="bottom-right"
                                    variant="accent"
                                    bgToken="danger"
                                />
                            )}
                        </Button> 
                        */}

                        {!project.isChannel && (
                            <Filter
                                workspaceId={workspaceId}
                                projectId={projectId}
                            />
                        )}
                    </ProjectHeader>

                    {/* 
                    <ToolHeader
                        view={selectedViewOption}
                        onViewChange={handleViewChange}
                    /> 
                   
 */}
                    <View
                        column
                        flex={1}
                        width="100%"
                        height="100%"
                        position="relative"
                        justifyContent="stretch">
                        <Error error={error} />
                        {/* make sure the right project has loaded */}
                        {!loading && !!project.id && projectId == project.id && (
                            <View
                                width="100%"
                                height="100%"
                                style={{ top: 0, left: 0 }}
                                position="absolute"
                                className="f-overflow-y-auto f-scrollbar">
                                {!project.isChannel && (
                                    <Tasks
                                        view={view}
                                        workspaceId={workspaceId}
                                        projectId={project.id}
                                        teamId={project.teamId}
                                        fields={fields}
                                        type="project"
                                    />
                                )}

                                {project.isChannel && (
                                    <Messages
                                        workspaceId={workspaceId}
                                        projectId={projectId}
                                        teamId={project.teamId}
                                    />
                                )}
                            </View>
                        )}
                    </View>
                </View>
            </View>

            <Outlet />

            {!!sidebar && <ProjectSidebar />}

    {/* 
            <Sidebar className={!!sidebar ? 'project-sidebar f-col is-open' : 'project-sidebar f-col'}>
                <Button
                    subtle
                    height="2rem"
                    p="0 0.5rem"
                    bgToken={sidebar == 'inbox' ? 'project-bg-active' : undefined}
                    colorToken={sidebar == 'inbox' ? 'accent' : undefined}
                    onClick={() => actions.setSidebar('inbox')}>
                    <Icon icon={TbInbox} />
                    {hasNotifications && (
                        <Badge
                            anchor="top-right"
                            variant="accent"
                            bgToken="danger"
                        />
                    )}
                </Button>

                {viewType != 'calendar' && (
                    <Button
                        subtle
                        height="2rem"
                        p="0 0.5rem"
                        bgToken={sidebar == 'calendar' ? 'project-bg-active' : undefined}
                        colorToken={sidebar == 'calendar' ? 'accent' : undefined}
                        onClick={handleCalendarOpen}>
                        <Icon
                            icon={TbCalendar}
                            className="tabler-calendar-icon"
                        />
                    </Button>
                )}

                {project.isChannel && (
                    <Button
                        subtle
                        height="2rem"
                        p="0 0.5rem"
                        bgToken={sidebar == 'tasks' ? 'project-bg-active' : undefined}
                        colorToken={sidebar == 'tasks' ? 'accent' : undefined}
                        onClick={handleTasksOpen}>
                        <Icon icon={TbSquareRoundedCheck} />
                    </Button>
                )}

                {!project.isChannel && (
                    <Button
                        subtle
                        height="2rem"
                        p="0 0.5rem"
                        bgToken={sidebar == 'messages' ? 'project-bg-active' : undefined}
                        colorToken={sidebar == 'messages' ? 'accent' : undefined}
                        onClick={handleMessagesOpen}>
                        <Icon icon={TbMessageCircle} />
                        {hasMessageNotifications && (
                            <Badge
                                anchor="top-right"
                                variant="accent"
                                bgToken="danger"
                            />
                        )}
                    </Button>
                )}

                <Button
                    subtle
                    height="2rem"
                    p="0 0.5rem"
                    bgToken={sidebar == 'overview' ? 'project-bg-active' : undefined}
                    colorToken={sidebar == 'overview' ? 'accent' : undefined}
                    onClick={() => actions.setSidebar('overview')}>
                    <Icon icon={TbNote} />
                </Button>
                <Flexer />
            </Sidebar> 
            */}
        </>
    )
}
